import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { getCache, setCache } from '../utils/cache';
import RenderMonitor from '../components/RenderMonitor';
import BootcampReflectionsSection from '../components/bootcamp/BootcampReflectionsSection';
import { useOptionalAdminAnalyticsHierarchy } from '../contexts/AdminAnalyticsHierarchyContext';
import { useOptionalManagerHierarchy } from '../contexts/ManagerHierarchyContext';
import { writeAdminAnalyticsSelection, writeManagerSelection } from '../utils/managerAccess';
import { getEffectiveIsGlobal } from '../utils/adminViewMode';
import checkpointIcon from '../assets/checkpoint.svg';
import crossIcon from '../assets/cross.svg';

function LockIcon({ color = '#9ca3af' }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill={color}
        d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2Zm-7-2a2 2 0 1 1 4 0v2h-4V7Zm7 12H7v-8h10v8Z"
      />
    </svg>
  );
}

function KpiCircle({ pct, colorForPct, locked = false }) {
  const size = 84;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
  const dash = (clamped / 100) * c;
  const color = locked ? 'rgba(100,116,139,0.25)' : colorForPct(clamped);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} stroke="rgba(100,116,139,0.2)" strokeWidth={stroke} fill="none" />
      <circle
        cx={size/2}
        cy={size/2}
        r={r}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${c - dash}`}
        transform={`rotate(-90 ${size/2} ${size/2})`}
      />
      {locked ? (
        <g transform={`translate(${(size / 2) - 11}, ${(size / 2) - 11})`} opacity="0.9">
          <LockIcon color="#9ca3af" />
        </g>
      ) : (
        <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fontSize="16" fontWeight="700" fill="#111827">{clamped}%</text>
      )}
    </svg>
  );
}

function userLabel(uid, idToInfo) {
  const info = idToInfo[String(uid)] || {};
  return info.name || info.email || String(uid).slice(0, 8);
}

function CollapsibleUserList({ listKey, label, userIds, idToInfo, expanded, onToggle, status = 'complete', onUserClick }) {
  const count = userIds.length;
  if (count === 0) return null;

  const isComplete = status === 'complete';
  const icon = isComplete ? checkpointIcon : crossIcon;

  return (
    <div className="bootcamp-user-list-group">
      <button
        type="button"
        className={`bootcamp-user-list-toggle bootcamp-user-list-toggle--${status}${expanded ? ' bootcamp-user-list-toggle--expanded' : ''}`}
        onClick={() => onToggle(listKey)}
        aria-expanded={expanded}
      >
        <img src={icon} alt="" aria-hidden="true" className="bootcamp-user-list-toggle-icon" />
        <span className="bootcamp-user-list-toggle-text">
          <span className="bootcamp-user-list-toggle-label">{label}</span>
          <span className="bootcamp-user-list-toggle-meta">
            {count} user{count !== 1 ? 's' : ''} · {expanded ? 'Hide' : 'Show'}
          </span>
        </span>
      </button>
      {expanded && (
        <div className="modern-flex modern-flex-wrap modern-gap-2 bootcamp-user-list-tags">
          {userIds.map((uid) => (
            <button
              key={`${listKey}-${uid}`}
              type="button"
              className="modern-user-tag modern-user-tag-clickable"
              onClick={() => onUserClick?.(uid)}
            >
              {userLabel(uid, idToInfo)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BootcampDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const adminAnalyticsHierarchy = useOptionalAdminAnalyticsHierarchy();
  const managerHierarchy = useOptionalManagerHierarchy();
  const analyticsHierarchy = adminAnalyticsHierarchy || managerHierarchy;
  const passedUserCount = location?.state?.userCount;
  const [leaderboard, setLeaderboard] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [lessonProgress, setLessonProgress] = useState([]);
  const [bootcamp, setBootcamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [leaderboardExpanded, setLeaderboardExpanded] = useState(false);
  const [expandedUserLists, setExpandedUserLists] = useState({});

  const LEADERBOARD_PREVIEW = 5;

  const toggleUserList = (listKey) => {
    setExpandedUserLists((prev) => ({ ...prev, [listKey]: !prev[listKey] }));
  };
  const analyticsBack = useMemo(() => {
    const portal = searchParams.get('portal');
    const inferredBasePath = portal === 'manager'
      ? '/manager'
      : portal === 'admin'
        ? '/admin/analytics'
        : managerHierarchy
          ? '/manager'
          : '/admin/analytics';

    const stateBack = location?.state?.analyticsBack;
    if (stateBack) {
      return {
        ...stateBack,
        basePath: stateBack.basePath || inferredBasePath,
      };
    }

    const clientIdParam = searchParams.get('client');
    if (!clientIdParam) return null;
    return {
      clientId: clientIdParam,
      regionId: searchParams.get('region') || '',
      regionName: analyticsHierarchy?.selectedRegion?.name || 'Region',
      basePath: inferredBasePath,
    };
  }, [
    location?.state?.analyticsBack,
    searchParams,
    analyticsHierarchy?.selectedRegion?.name,
    managerHierarchy,
  ]);

  // Helper to parse stutter_time interval
  const parseIntervalToMs = (interval) => {
    // Match days (optional), then HH:MM:SS (optional)
    const dayMatch = interval.match(/(\d+)\s+days?/);
    const timeMatch = interval.match(/(\d{1,2}):(\d{2}):(\d{2})/);
    let ms = 0;
    if (dayMatch) {
      ms += parseInt(dayMatch[1], 10) * 24 * 60 * 60 * 1000;
    }
    if (timeMatch) {
      ms += parseInt(timeMatch[1], 10) * 60 * 60 * 1000; // hours
      ms += parseInt(timeMatch[2], 10) * 60 * 1000;      // minutes
      ms += parseInt(timeMatch[3], 10) * 1000;           // seconds
    }
    return ms;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      // Determine if user is global admin to control edit UI and RPC choice
      let isGlobalLocal = false;
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;
        if (userId) {
          const { data: lean } = await supabase
            .from('lean_admin_users')
            .select('is_global')
            .eq('user_id', userId)
            .eq('is_global', true)
            .maybeSingle();
          isGlobalLocal = getEffectiveIsGlobal(!!lean);
          setIsGlobalAdmin(isGlobalLocal);
        }
      } catch {}
      const CACHE_KEY = `bootcamp.${id}.detail.v2`;
      const ttlMs = 2 * 60 * 1000;
      const cached = getCache(CACHE_KEY, ttlMs);
      if (cached) {
        // Apply snapshot from cache
        if (cached.bootcamp) setBootcamp(cached.bootcamp);
        if (Array.isArray(cached.leaderboard)) setLeaderboard(cached.leaderboard);
        if (Array.isArray(cached.lessons)) {
          const sortedCachedLessons = (cached.lessons || []).slice().sort((a, b) => {
            const ao = a.lesson_type === 'onboarding' ? 0 : 1;
            const bo = b.lesson_type === 'onboarding' ? 0 : 1;
            if (ao !== bo) return ao - bo;
            const ap = typeof a.position === 'number' ? a.position : 0;
            const bp = typeof b.position === 'number' ? b.position : 0;
            return ap - bp;
          });
          setLessons(sortedCachedLessons);
        }
        if (Array.isArray(cached.lessonProgress)) setLessonProgress(cached.lessonProgress);
        // Build teamUsers array from cached teamUserCount (we only need length for denominator)
        const cnt = typeof cached.teamUserCount === 'number' ? cached.teamUserCount : 0;
        setTeamUsers(new Array(cnt).fill(0).map((_, i) => ({ user_id: `cache-${i}` })));
        // If navigation provided a fresher userCount, prefer it
        const passedCount = passedUserCount;
        if (typeof passedCount === 'number') {
          setTeamUsers(new Array(passedCount).fill(0).map((_, i) => ({ user_id: `state-${i}` })));
        }
        setLoading(false);
        return;
      }
      // Get bootcamp info
      console.log('[BOOTCAMP][DB] SELECT bootcamp info');
      const { data: bootcampData, error: bootcampError } = await supabase
        .from('bootcamps')
        .select('id, start_date, end_date, stutter_time, team_id, course_id, courses(name), teams(name, client_id)')
        .eq('id', id)
        .single();
      if (bootcampError) {
        setError('Could not fetch bootcamp');
        setLoading(false);
        return;
      }
      setBootcamp(bootcampData);
      console.log(bootcampData)
      // Debug: log the bootcamp id being passed
      const rpcName = isGlobalLocal ? 'get_leaderboard' : 'get_leaderboard_manager';
      console.log('[BOOTCAMP][DB] RPC', rpcName, { p_bootcamp_id: Number(id) });
      // Get leaderboard (now includes email)
      const { data: leaderboardData, error: leaderboardError } = await supabase
        .rpc(rpcName, { p_bootcamp_id: Number(id) });
      // Debug: log the result of the RPC call
      if (leaderboardError) {
        console.error('Leaderboard RPC error:', leaderboardError);
      } else {
        console.log('Leaderboard data:', leaderboardData);
      }
      if (leaderboardError) {
        setError('Could not fetch leaderboard');
        setLoading(false);
        return;
      }
      setLeaderboard(leaderboardData || []);
      // Get lessons for this course
      console.log('[BOOTCAMP][DB] SELECT lessons for course', { course_id: bootcampData.course_id });
      const { data: lessonsData, error: lessonsError } = await supabase
        .from('lessons')
        .select('lesson_id, position, title, lesson_type')
        .eq('course_id', bootcampData.course_id)
        .order('position', { ascending: true });
      if (lessonsError) {
        setError('Could not fetch lessons');
        setLoading(false);
        return;
      }
      // Ensure onboarding lesson appears first
      const sortedLessons = (lessonsData || []).slice().sort((a, b) => {
        const ao = a.lesson_type === 'onboarding' ? 0 : 1;
        const bo = b.lesson_type === 'onboarding' ? 0 : 1;
        if (ao !== bo) return ao - bo;
        // then by position
        const ap = typeof a.position === 'number' ? a.position : 0;
        const bp = typeof b.position === 'number' ? b.position : 0;
        return ap - bp;
      });
      setLessons(sortedLessons);
      // Determine team user count: prefer passed from navigation; else fallback to leaderboard size after we fetch it
      let teamUserCount = passedUserCount;
      if (typeof teamUserCount === 'number') {
        setTeamUsers(new Array(teamUserCount).fill(0).map((_, i) => ({ user_id: `local-${i}` })));
      } else {
        teamUserCount = null; // will set after leaderboard fetch below
      }
      // Get per-lesson progress via RPC (includes quiz/reflections and user id lists)
      console.log('[BOOTCAMP][DB] RPC bootcamp_lesson_progress', { bootcamp_id: id });
      const { data: progressData, error: progressError } = await supabase
        .rpc('bootcamp_lesson_progress', { p_bootcamp_id: Number(id) });
      if (progressError) {
        setError('Could not fetch lesson progress');
        setLoading(false);
        return;
      }
      setLessonProgress(Array.isArray(progressData) ? progressData : []);
      // If we didn't have teamUserCount passed in, infer it from leaderboard size
      if (teamUserCount == null) {
        setTeamUsers((leaderboardData || []).map(u => ({ user_id: u.id || u.user_id })));
        teamUserCount = (leaderboardData || []).length;
      }
      // Snapshot to cache
      setCache(CACHE_KEY, {
        bootcamp: bootcampData,
        leaderboard: leaderboardData || [],
        lessons: lessonsData || [],
        lessonCompletions: [],
        lessonProgress: Array.isArray(progressData) ? progressData : [],
        teamUserCount: teamUserCount || 0,
      });
      setLoading(false);
    };
    fetchData();
  }, [id, passedUserCount]);

  useEffect(() => {
    if (location.hash !== '#reflections' || loading) return;
    const el = document.getElementById('reflections');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, [location.hash, loading]);

  const reflectionUsers = useMemo(() => {
    const fromState = location?.state?.users;
    if (Array.isArray(fromState) && fromState.length > 0) return fromState;
    return (leaderboard || []).map((u) => ({
      id: u.id || u.user_id,
      full_name: u.full_name,
      email: u.email,
    }));
  }, [leaderboard, location?.state?.users]);

  const isFinishedBootcamp = (() => {
    try {
      return bootcamp?.end_date ? new Date(bootcamp.end_date).getTime() < Date.now() : false;
    } catch {
      return false;
    }
  })();

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(new Date(date));
  };

  if (loading) return <div className="dashboard-page"><div className="modern-loading">Loading bootcamp...</div></div>;
  if (error) return <div className="dashboard-page"><div className="modern-error">{error}</div></div>;

  // Calculate lesson start/expiry times
  const stutterMs = parseIntervalToMs(bootcamp.stutter_time);
  const bootcampStart = new Date(bootcamp.start_date).getTime();
  // Helper: compute timing per lesson using new logic (one stutter window per lesson)
  const getLessonTiming = (l) => {
    if (!l) return { start: null, expiry: null };
    if (l.lesson_type === 'onboarding') {
      return { start: new Date(bootcamp.start_date), expiry: new Date(bootcamp.end_date) };
    }
    const pos = Number(l.position || 1);
    const start = new Date(bootcampStart + ((pos - 1) * stutterMs));
    const expiry = new Date(bootcampStart + (pos * stutterMs));
    return { start, expiry };
  };

  // KPI calculations
  const colorForPct = (pct) => {
    const hue = Math.max(0, Math.min(120, Math.round((pct / 100) * 120)));
    return `hsl(${hue}, 70%, 40%)`;
  };
  // Helper: get timing for a lesson (open lessons only; onboarding excluded for this KPI helper)
  const getTimingForLesson = (lesson) => {
    if (!lesson || lesson.lesson_type === 'onboarding') return { start: null, expiry: null };
    const pos = Number(lesson.position || 1);
    const start = new Date(bootcampStart + ((pos - 1) * stutterMs));
    const expiry = new Date(bootcampStart + (pos * stutterMs));
    return { start, expiry };
  };
  // Onboarding percent
  const onboardingLesson = (lessons || []).find(l => l.lesson_type === 'onboarding');
  const onboardingProgress = (() => {
    if (!onboardingLesson) return { pct: 0, done: 0, total: (lessonProgress?.[0]?.total_users || 0) };
    const p = (lessonProgress || []).find(lp => lp.lesson_id === onboardingLesson.lesson_id) || {};
    const total = Number(p.total_users || 0);
    const done = Number(p.users_with_completion || 0);
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return { pct, done, total };
  })();
  // Next closing checkpoint (oldest open by earliest expiry)
  const now = Date.now();
  const openWithExpiry = (lessons || [])
    .filter(l => l.lesson_type !== 'onboarding')
    .map(l => ({ l, t: getTimingForLesson(l) }))
    .filter(({ t }) => t.start && t.expiry && now >= t.start.getTime() && now < t.expiry.getTime())
    .sort((a, b) => (a.t.expiry.getTime() - b.t.expiry.getTime()));
  const nextClosingLesson = openWithExpiry.length > 0 ? openWithExpiry[0].l : null;
  const nextClosingIds = (leaderboard || []).map(u => String(u.id || u.user_id));
  const nextClosingName = nextClosingLesson ? (nextClosingLesson.title || `Checkpoint ${nextClosingLesson.position}`) : 'N/A';
  const nextClosingMetrics = (() => {
    if (!nextClosingLesson) return { startedPct: 0, startedDone: 0, startedTotal: 0, allDonePct: 0, allDone: 0 };
    const p = (lessonProgress || []).find(lp => lp.lesson_id === nextClosingLesson.lesson_id) || {};
    const total = Number(p.total_users || nextClosingIds.length || 0);
    const startedDone = Number(p.users_with_completion || 0);
    const startedPct = total > 0 ? Math.round((startedDone / total) * 100) : 0;
    const missingComp = new Set(Array.isArray(p.users_missing_completion) ? p.users_missing_completion.map(String) : []);
    const missingRefl = new Set(Array.isArray(p.users_missing_reflections) ? p.users_missing_reflections.map(String) : []);
    let allDone = 0;
    for (const uid of nextClosingIds) {
      if (!missingComp.has(uid) && !missingRefl.has(uid)) allDone += 1;
    }
    const allDonePct = total > 0 ? Math.round((allDone / total) * 100) : 0;
    return { startedPct, startedDone, startedTotal: total, allDonePct, allDone };
  })();

  const courseName = bootcamp.courses?.name || 'Bootcamp';
  const teamName = bootcamp.teams?.name || 'Team';
  const visibleLeaderboard = leaderboardExpanded
    ? leaderboard
    : (leaderboard || []).slice(0, LEADERBOARD_PREVIEW);
  const hasMoreLeaderboard = (leaderboard || []).length > LEADERBOARD_PREVIEW;

  const navigateToUser = (userOrId) => {
    const uid = userOrId != null && typeof userOrId === 'object'
      ? (userOrId.id || userOrId.user_id)
      : userOrId;
    if (!uid) return;
    const params = searchParams.toString();
    navigate({
      pathname: `/bootcamp/${id}/user/${uid}`,
      search: params ? `?${params}` : '',
      state: location.state,
    });
  };

  const handleBack = () => {
    if (analyticsBack?.clientId) {
      const basePath = analyticsBack.basePath || '/admin/analytics';
      const writeSelection = basePath === '/manager'
        ? writeManagerSelection
        : writeAdminAnalyticsSelection;
      const selection = {
        client: analyticsBack.clientId,
        region: analyticsBack.regionId || '',
        team: '',
        user: '',
      };
      writeSelection(selection);
      const params = new URLSearchParams();
      params.set('client', analyticsBack.clientId);
      if (analyticsBack.regionId) params.set('region', analyticsBack.regionId);
      navigate(`${basePath}?${params.toString()}`, { replace: true });
      return;
    }
    const clientId = bootcamp?.teams?.client_id;
    if (!clientId) {
      navigate(-1);
      return;
    }
    if (isGlobalAdmin) navigate(`/client/${clientId}`);
    else navigate(`/manager?client=${clientId}`);
  };

  const backLabel = analyticsBack?.regionName
    ? `← ${analyticsBack.regionName}`
    : '← Back';

  return (
    <div className="dashboard-page">
      <RenderMonitor componentName="BootcampDetail" />

      <button type="button" className="analytics-back-link" onClick={handleBack}>
        {backLabel}
      </button>

      <div className="modern-mb-4">
        <h1 className="modern-title" style={{ marginBottom: 8 }}>{courseName} analytics</h1>
        <p className="modern-subtitle" style={{ marginBottom: 0 }}>Activity Overview</p>
      </div>

      <div className="analytics-kpi-section">
        <div className="analytics-kpi-grid">
          <div className="analytics-kpi-card">
            <div className="analytics-kpi-label">Team</div>
            <div className="analytics-kpi-meta-value">{teamName}</div>
          </div>
          <div className="analytics-kpi-card">
            <div className="analytics-kpi-label">Bootcamp</div>
            <div className="analytics-kpi-meta-value">{courseName}</div>
          </div>
          <div className="analytics-kpi-card">
            <div className="analytics-kpi-label">Start Date</div>
            <div className="analytics-kpi-meta-value">{formatDate(bootcamp.start_date)}</div>
          </div>
          <div className="analytics-kpi-card">
            <div className="analytics-kpi-label">End Date</div>
            <div className="analytics-kpi-meta-value">{formatDate(bootcamp.end_date)}</div>
          </div>
        </div>
      </div>

      {!isFinishedBootcamp && (
        <>
          <h2 className="modern-heading analytics-section-heading modern-mb-4">Key Performance Indicators</h2>
          <div className="analytics-kpi-section">
            <div className="analytics-kpi-grid">
              <div className="analytics-kpi-card analytics-kpi-card--centered">
                <a href="#section-onboarding" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                  <KpiCircle pct={onboardingProgress.pct} colorForPct={colorForPct} />
                  <div className="analytics-kpi-label" style={{ marginTop: 10, marginBottom: 0 }}>Onboarding Completed</div>
                </a>
              </div>
              {new Date(bootcamp.start_date) <= new Date() && (
                <>
                  <div className="analytics-kpi-card analytics-kpi-card--centered">
                    {nextClosingLesson ? (
                      <a href={`#section-lesson-${nextClosingLesson.lesson_id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <KpiCircle pct={nextClosingMetrics.startedPct} colorForPct={colorForPct} />
                        <div className="analytics-kpi-label" style={{ marginTop: 10, marginBottom: 0 }}>Started {nextClosingName}</div>
                      </a>
                    ) : (
                      <>
                        <KpiCircle pct={nextClosingMetrics.startedPct} colorForPct={colorForPct} />
                        <div className="analytics-kpi-label" style={{ marginTop: 10, marginBottom: 0 }}>Started {nextClosingName}</div>
                      </>
                    )}
                  </div>
                  <div className="analytics-kpi-card analytics-kpi-card--centered">
                    {nextClosingLesson ? (
                      <a href={`#section-lesson-${nextClosingLesson.lesson_id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <KpiCircle pct={nextClosingMetrics.allDonePct} colorForPct={colorForPct} />
                        <div className="analytics-kpi-label" style={{ marginTop: 10, marginBottom: 0 }}>Finished {nextClosingName}</div>
                      </a>
                    ) : (
                      <>
                        <KpiCircle pct={nextClosingMetrics.allDonePct} colorForPct={colorForPct} />
                        <div className="analytics-kpi-label" style={{ marginTop: 10, marginBottom: 0 }}>Finished {nextClosingName}</div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      <h2 className="modern-heading analytics-section-heading modern-mb-4 modern-mt-6">Engagement Rates</h2>
      <div className="analytics-kpi-section">
        <div className="analytics-kpi-grid">
          {(lessons || []).map((lesson) => {
            const isOnboarding = lesson.lesson_type === 'onboarding';
            const p = (lessonProgress || []).find(lp => lp.lesson_id === lesson.lesson_id) || {};
            const totalUsers = Number(p.total_users || teamUsers.length || 0);
            const withCompletion = Number(p.users_with_completion || 0);
            const pct = totalUsers > 0 ? Math.round((withCompletion / totalUsers) * 100) : 0;

            const { start: lessonStart } = getLessonTiming(lesson);
            const nowTs = Date.now();
            const locked = !isOnboarding && lessonStart && nowTs < lessonStart.getTime();
            const label = lesson.title ? lesson.title : (isOnboarding ? 'Onboarding' : `Checkpoint ${lesson.position}`);

            return (
              <div
                key={`eng-${lesson.lesson_id}`}
                className={`analytics-kpi-card analytics-kpi-card--centered${locked ? ' analytics-kpi-card--locked' : ''}`}
              >
                <KpiCircle pct={locked ? 0 : pct} colorForPct={colorForPct} locked={locked} />
                <div className="analytics-kpi-label" style={{ marginTop: 10, marginBottom: 0 }}>{label}</div>
                {locked ? (
                  <div className="analytics-kpi-meta-value" style={{ marginTop: 4, fontSize: '0.8rem' }}>
                    Unlocks {lessonStart ? formatDate(lessonStart) : ''}
                  </div>
                ) : (
                  <div className="analytics-kpi-meta-value" style={{ marginTop: 4, fontSize: '0.8rem' }}>
                    {pct}% ({withCompletion}/{totalUsers})
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <h2 className="modern-heading analytics-section-heading modern-mb-4">Team Leaderboard</h2>
      <div className="modern-list-panel">
        {visibleLeaderboard.length === 0 ? (
          <div className="modern-list-empty">No leaderboard data yet.</div>
        ) : (
          visibleLeaderboard.map((user, idx) => {
            const userId = user.id || user.user_id;
            return (
              <div
                key={userId || idx}
                className="modern-list-row modern-list-row-clickable"
                onClick={() => navigateToUser(user)}
              >
                <span className="modern-list-row-label bootcamp-leaderboard-user-label">
                  {user.icon && (
                    <img
                      src={user.icon}
                      alt=""
                      style={{ width: 24, height: 24, borderRadius: '50%', marginRight: 8, verticalAlign: 'middle' }}
                    />
                  )}
                  {user.full_name ? user.full_name : user.email}
                </span>
                <span className="analytics-list-row-points">{user.points}</span>
              </div>
            );
          })
        )}
      </div>
      {hasMoreLeaderboard && (
        <button
          type="button"
          className="analytics-expand-link"
          onClick={() => setLeaderboardExpanded((v) => !v)}
        >
          {leaderboardExpanded
            ? 'Show top 5'
            : `Show all ${leaderboard.length} users`}
        </button>
      )}
      <h2 className="modern-heading analytics-section-heading modern-mb-4 modern-mt-6">Checkpoints</h2>
      <div className="modern-mb-6">
        <div className="modern-flex modern-flex-col modern-gap-3">
          {lessons.map((lesson, idx) => {
          // Find progress entry for this lesson
          const p = (lessonProgress || []).find(lp => lp.lesson_id === lesson.lesson_id) || {};
          const totalUsers = Number(p.total_users || teamUsers.length || 0);
          const withCompletion = Number(p.users_with_completion || 0);
          const missingCompletionIds = Array.isArray(p.users_missing_completion) ? p.users_missing_completion : [];
          const withReflections = Number(p.users_with_reflections || 0);
          const missingReflectionIds = Array.isArray(p.users_missing_reflections) ? p.users_missing_reflections : [];
          // Map user ids to display name/email via leaderboard (fallback to id)
          const idToInfo = (leaderboard || []).reduce((acc, u) => { acc[String(u.id || u.user_id)] = { name: u.full_name || u.email || '', email: u.email || '' }; return acc; }, {});

          // Calculate lesson start and expiry per new logic (one stutter_time)
          const isOnboarding = lesson.lesson_type === 'onboarding';
          const { start: lessonStart, expiry: lessonExpiry } = getLessonTiming(lesson);
          const nowTs = Date.now();
          let status = 'locked';
          let statusLabel = 'Locked';
          if (!isOnboarding && lessonStart && lessonExpiry) {
            const startTs = lessonStart.getTime();
            const expiryTs = lessonExpiry.getTime();
            if (nowTs >= startTs && nowTs < expiryTs) { status = 'open'; statusLabel = 'Open'; }
            else if (nowTs >= expiryTs) { status = 'closed'; statusLabel = 'Closed'; }
          }
          const isLocked = (status === 'locked') && !isOnboarding;

          // Progress values
          const pctQuiz = totalUsers > 0 ? Math.round((withCompletion / totalUsers) * 100) : 0;
          const pctRef = totalUsers > 0 ? Math.round((withReflections / totalUsers) * 100) : 0;
          const colorForPct = (pct) => {
            const hue = Math.max(0, Math.min(120, Math.round((pct / 100) * 120)));
            return `hsl(${hue}, 70%, 40%)`;
          };
          const barOuter = { background: 'rgba(100,116,139,0.15)', height: 10, borderRadius: 999, overflow: 'hidden' };
          const barInner = (pct) => ({ width: `${pct}%`, height: '100%', background: colorForPct(pct), opacity: 0.95 });
          const barContainer = { width: 320, maxWidth: '100%' };

          const allUserIds = (leaderboard || []).map((u) => String(u.id || u.user_id)).filter(Boolean);
          const missingCompletionSet = new Set(missingCompletionIds.map(String));
          const missingReflectionSet = new Set(missingReflectionIds.map(String));
          const completedIds = allUserIds.filter((uid) => !missingCompletionSet.has(uid));
          const reflectedIds = allUserIds.filter((uid) => !missingReflectionSet.has(uid));
          const completionDoneLabel = isOnboarding ? 'Finished onboarding' : 'Finished quiz';
          const completionPendingLabel = isOnboarding ? 'Has not finished onboarding' : 'Has not finished quiz';

          const displayTitle = lesson.title
            || (isOnboarding ? 'Onboarding' : `Checkpoint ${lesson.position}`);

          const anchorId = isOnboarding ? 'section-onboarding' : `section-lesson-${lesson.lesson_id}`;
          return (
            <div key={lesson.lesson_id} id={anchorId} className="modern-list-panel bootcamp-checkpoint-panel">
              <div className="bootcamp-checkpoint-header">
                <div className="bootcamp-checkpoint-title-wrap">
                  <h3 className="bootcamp-checkpoint-title">{displayTitle}</h3>
                  {isOnboarding && lesson.title && (
                    <span className="bootcamp-checkpoint-subtitle">Onboarding</span>
                  )}
                </div>
                {!isOnboarding && (
                  <span className={`bootcamp-checkpoint-status bootcamp-checkpoint-status--${status}`}>
                    {statusLabel}
                  </span>
                )}
              </div>
              {!isOnboarding && (
                <div className="bootcamp-checkpoint-dates">
                  Start: {lessonStart ? formatDate(lessonStart) : ''} · Expiry: {lessonExpiry ? formatDate(lessonExpiry) : ''}
                </div>
              )}

              {(!isLocked || isOnboarding) && (
                <>
                  {/* Progress Bars */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <div style={{ minWidth: 180, color: '#334155', fontSize: 14, fontWeight: 600 }}>{isOnboarding ? 'Onboarding completed' : 'Quiz completed'}</div>
                    <div style={barContainer}>
                      <div style={barOuter}><div style={barInner(pctQuiz)} /></div>
                    </div>
                    <div style={{ minWidth: 80, textAlign: 'right', color: '#334155', fontWeight: 600 }}>{pctQuiz}% ({withCompletion}/{totalUsers})</div>
                  </div>
                  <div className="bootcamp-user-lists">
                    <CollapsibleUserList
                      listKey={`${lesson.lesson_id}-completion-done`}
                      label={completionDoneLabel}
                      userIds={completedIds}
                      idToInfo={idToInfo}
                      expanded={!!expandedUserLists[`${lesson.lesson_id}-completion-done`]}
                      onToggle={toggleUserList}
                      status="complete"
                      onUserClick={navigateToUser}
                    />
                    <CollapsibleUserList
                      listKey={`${lesson.lesson_id}-completion-pending`}
                      label={completionPendingLabel}
                      userIds={missingCompletionIds}
                      idToInfo={idToInfo}
                      expanded={!!expandedUserLists[`${lesson.lesson_id}-completion-pending`]}
                      onToggle={toggleUserList}
                      status="incomplete"
                      onUserClick={navigateToUser}
                    />
                  </div>

                  {!isOnboarding && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                        <div style={{ minWidth: 180, color: '#334155', fontSize: 14, fontWeight: 600 }}>Reflections completed</div>
                        <div style={barContainer}>
                          <div style={barOuter}><div style={barInner(pctRef)} /></div>
                        </div>
                        <div style={{ minWidth: 80, textAlign: 'right', color: '#334155', fontWeight: 600 }}>{pctRef}% ({withReflections}/{totalUsers})</div>
                      </div>
                      <div className="bootcamp-user-lists">
                        <CollapsibleUserList
                          listKey={`${lesson.lesson_id}-reflection-done`}
                          label="Finished reflections"
                          userIds={reflectedIds}
                          idToInfo={idToInfo}
                          expanded={!!expandedUserLists[`${lesson.lesson_id}-reflection-done`]}
                          onToggle={toggleUserList}
                          status="complete"
                          onUserClick={navigateToUser}
                        />
                        <CollapsibleUserList
                          listKey={`${lesson.lesson_id}-reflection-pending`}
                          label="Has not finished reflections"
                          userIds={missingReflectionIds}
                          idToInfo={idToInfo}
                          expanded={!!expandedUserLists[`${lesson.lesson_id}-reflection-pending`]}
                          onToggle={toggleUserList}
                          status="incomplete"
                          onUserClick={navigateToUser}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          );
        })}
        </div>
      </div>

      <h2 id="reflections" className="modern-heading analytics-section-heading modern-mb-4 modern-mt-6">User Reflections</h2>
      <BootcampReflectionsSection
        bootcampId={id}
        bootcamp={bootcamp}
        lessons={lessons}
        initialUsers={reflectionUsers}
        onUserClick={navigateToUser}
      />
    </div>
  );
}

export default BootcampDetail; 